import { toast } from 'react-toastify';
import API from '../apiClient';
import FileUploadAPI from '../FileUploadAPI';
import { handleApiError } from '../Utils/errorHandler';
import { hasDocxOrDocExtension } from '../Utils/fileValidation';

export const GetPolicyCenterData = async (org_id, type, search_pattern, policy_set_id) => {
  try {
    const queryParams = new URLSearchParams({ organization_id: org_id });

    if (type) {
      queryParams.append("type", type);
    }

    if (search_pattern) {
      queryParams.append("search_pattern", search_pattern);
    }

    // Template-pack runs: list only the documents of one generated policy set
    if (policy_set_id) {
      queryParams.append("policy_set_id", policy_set_id);
    }

    const queryString = queryParams.toString();
    const response = await API.get(`/policy_center/policies?${queryString}`);
    return response;
  } catch (error) {
    throw handleApiError(error, 'Failed to get policy center data', { organization_id: org_id });
  }
};

export const addPolicyCenter = async (payload) => {
  try {
    const response = await API.post(`/policy_center/policies`, payload);
    return response;
  } catch (error) {
    throw handleApiError(error, 'Failed to create policy center');
  }
};

export const UpdatePolicyCenter = async (entity, payload) => {
  try {
    const isBeingApproved = payload.status === "Approved";
    const hasFile = payload.file_id !== null && payload.file_id !== undefined;
    
    if (isBeingApproved && hasFile) {
      const policyResponse = await API.get(`/policy_center/policies/${encodeURIComponent(entity)}`);
      const fileData = policyResponse?.data?.sas_url || '';
      const fileName = policyResponse?.data?.file_name || '';
      const isValidDocFile = hasDocxOrDocExtension(fileName) || hasDocxOrDocExtension(fileData);
      
      if (isBeingApproved && hasFile && !isValidDocFile) {
        console.log("File is not a docx/doc, proceeding without highlight removal");
      }
      
      try {
        const response = await API.put(`/policy_center/policies/${encodeURIComponent(entity)}`, payload);
        return response;
      } catch (error) {
        // Check if backend created a new draft from stale ID (returns 200 with redirect_to_id)
        if (error?.response?.status === 200 && error?.response?.data?.redirect_to_id) {
          return error.response;
        }
        
        // Handle stale policy_center_id errors - silently refresh page
        if (error?.response?.status === 404 || error?.response?.status === 500) {
          const errorMsg = error?.response?.data?.error || '';
          if (errorMsg.includes('record not found') || errorMsg.includes('policy_center_id')) {
            console.log('Stale policy_center_id detected - refreshing page');
            // Return a pseudo-success response with refresh flag
            return { 
              status: 200, 
              data: { ...error.response?.data, needsRefresh: true },
              needsRefresh: true 
            };
          }
        }
        
        if (error?.response?.data?.error?.includes("failed to remove highlights")) {
          console.error("Error removing highlights:", error.response.data.error);
          if (!isValidDocFile) {
            return { ...error.response, status: 200, message: "Updated policy but skipped highlight removal" };
          }
        }
        throw handleApiError(error, 'Failed to update policy center', { policy_center_id: entity });
      }
    } else {
      const response = await API.put(`/policy_center/policies/${encodeURIComponent(entity)}`, payload);
      return response;
    }
  } catch (error) {
    // Check if backend created a new draft from stale ID (returns 200 with redirect_to_id)
    if (error?.response?.status === 200 && error?.response?.data?.redirect_to_id) {
      return error.response;
    }
    
    // Handle stale policy_center_id errors - silently refresh page
    if (error?.response?.status === 404 || error?.response?.status === 500) {
      const errorMsg = error?.response?.data?.error || '';
      if (errorMsg.includes('record not found') || errorMsg.includes('policy_center_id')) {
        console.log('Stale policy_center_id detected - refreshing page');
        // Return a pseudo-success response with refresh flag
        return { 
          status: 200, 
          data: { ...error.response?.data, needsRefresh: true },
          needsRefresh: true 
        };
      }
    }
    
    throw handleApiError(error, 'Failed to update policy center', { policy_center_id: entity });
  }
};

export const GetPolicyDetailByPolicyCenterID = async (entity) => {
  try {
    const response = await API.get(`/policy_center/policies/${encodeURIComponent(entity)}`);
    return response;
  } catch (error) {
    throw handleApiError(error, 'Failed to get policy center details', { policy_center_id: entity });
  }
};

/**
 * Deletes a Document Center row. `silent` is for cleanup nobody asked for (a file removed from a chat before
 * it was used): the raw error is rethrown and no error toast is shown, so the caller can log it.
 */
export const DeletePolicyCenter = async (entity, { silent = false } = {}) => {
  try {
    const response = await API.post(`/policy_center/policies/${encodeURIComponent(entity)}`);
    return response;
  } catch (error) {
    if (silent) throw error;
    throw handleApiError(error, 'Failed to delete policy center', { policy_center_id: entity });
  }
};

export const getPolicyTemplate = async (category, policyName) => {
  try {
    const response = await FileUploadAPI.get(`/policy_template/?template_category=${encodeURIComponent(category)}&search_pattern=${encodeURIComponent(policyName)}`);
    return response;
  } catch (error) {
    throw handleApiError(error, 'Failed to get policy template', { 
      category, 
      policy_name: policyName 
    });
  }
};

export const getPolicyPlaceholderPreview = async (policy_template_id, policy_center_id, account_id) => {
  try {
    const queryParams = new URLSearchParams({
      policy_template_id,
      policy_center_id,
      account_id
    });

    const response = await FileUploadAPI.get(`/policy/placeholder-preview?${queryParams.toString()}`);
    if (response.status === 200) {
      return response.data;
    }
    throw response;
  } catch (error) {
    throw handleApiError(error, 'Failed to fetch placeholder preview', {
      policy_template_id,
      policy_center_id,
      account_id
    });
  }
};

// Check if the current account has approval access (Leadership & Management)
export const getPolicyApprovalAccess = async (account_id) => {
  try {
    const response = await API.get(`/policy_center/policies/has-approval-access`, {
      params: { account_id }
    });
    return response;
  } catch (error) {
    throw handleApiError(error, 'Failed to check approval access', { account_id });
  }
};

export const createPolicyFile = async (payload) => {
  try {
    const formData = new FormData();
    
    for (const key in payload) {
      if (key === 'custom_placeholders' && typeof payload[key] === 'object') {
        formData.append(key, JSON.stringify(payload[key]));
      } else {
        formData.append(key, payload[key]);
      }
    }
    
    const response = await FileUploadAPI.post('/policy/create-policy-file', formData);
    if (response.status === 201) {
      return response;
    }
    throw response;
  } catch (error) {
    throw handleApiError(error, 'Failed to create policy file');
  }
};

export const generateStandardPolicy = async (template_category, type, organization_id, account_id, is_generate_policy_center) => {
  try {
    const response = await FileUploadAPI.post(`/policy_template/create-policy-set?template_category=${encodeURIComponent(template_category)}&type=${encodeURIComponent(type)}&organization_id=${encodeURIComponent(organization_id)}&account_id=${encodeURIComponent(account_id)}&is_generate_policy_center=${encodeURIComponent(is_generate_policy_center)}`);
    return response;
  } catch (error) {
    throw handleApiError(error, 'Failed to generate standard policy', {
      template_category,
      type,
      organization_id,
      account_id
    });
  }
};

export const getAllPlaceholders = async () => {
  try {
    const response = await FileUploadAPI.get('/policy/placeholders');
    if (response.status === 200) {
      return response.data;
    }
    throw response;
  } catch (error) {
    throw handleApiError(error, 'Failed to fetch placeholder options');
  }
};

export const getAllTemplates = async () => {
  try {
    const response = await FileUploadAPI.get('/policy/templates');
    if (response.status === 200) {
      return response.data;
    }
    throw response;
  } catch (error) {
    throw handleApiError(error, 'Failed to fetch template information');
  }
};

export const registerTemplateMapping = async (alternativeName, systemName) => {
  try {
    const queryParams = new URLSearchParams({
      alternative_name: alternativeName,
      system_name: systemName
    });

    const response = await FileUploadAPI.post(`/policy/register-template?${queryParams.toString()}`);
    if (response.status === 200) {
      return response.data;
    }
    throw response;
  } catch (error) {
    throw handleApiError(error, 'Failed to register template mapping', {
      alternative_name: alternativeName,
      system_name: systemName
    });
  }
};

export const generateBulkStandardPolicies = async (templates, organization_id, account_id) => {
  try {
    const promises = templates.map(template => 
      generateStandardPolicy(
        template.template_category || 'Standard',
        template.type || 'Document',
        organization_id,
        account_id,
        'true'
      )
    );
    
    const results = await Promise.all(promises);
    return { status: 200, data: results.flatMap(r => r.data || []) };
  } catch (error) {
    throw handleApiError(error, 'Failed to generate standard policies in bulk', {
      organization_id,
      account_id,
      template_count: templates.length
    });
  }
};

// Generate policy file from template with placeholders and create policy center
export const generatePolicyFileAndCreateCenter = async (payload) => {
  try {
    const response = await FileUploadAPI.post(`/policy/generate-policy-file`, payload, {
      headers: { 'Content-Type': 'application/json' }
    });
    return response;
  } catch (error) {
    throw handleApiError(error, 'Failed to generate policy file and create policy center');
  }
};

// Create a Policy Center draft from an approved Policy
export const CreateDraftFromPolicy = async (policyId) => {
  try {
    const response = await API.post(`/policy_center/policies/draft/from-policy/${encodeURIComponent(policyId)}`);
    return response;
  } catch (error) {
    throw handleApiError(error, 'Failed to create draft from policy', { policy_id: policyId });
  }
};

/**
 * GetPolicyCenterByRelatedModel — fetch all policy_center rows linked to a
 * specific IP asset record.  Used by the "Related Documents" tab in the IP
 * Asset Register modals and by the Document Center "Related Documents" tab.
 *
 * @param {string} relatedModelType — e.g. "brand_asset", "patent"
 * @param {string} relatedModelId   — the IP asset's UUID
 */
export const GetPolicyCenterByRelatedModel = async (relatedModelType, relatedModelId) => {
  try {
    const params = new URLSearchParams({
      related_model_type: relatedModelType,
      related_model_id: relatedModelId,
    });
    const response = await API.get(`/policy_center/policies/related?${params.toString()}`);
    return response;
  } catch (error) {
    // 204 No Content is a valid response (no linked documents yet)
    if (error?.response?.status === 204) return { data: [] };
    throw handleApiError(error, 'Failed to get related documents', { relatedModelType, relatedModelId });
  }
};

/**
 * GetAllPolicyCenterRelatedDocs — fetch all policy_center rows that have
 * related_model_type IS NOT NULL for the current org.  Used by the Document
 * Center "Related Documents" tab (global view across all IP assets).
 *
 * Calls the dedicated /related/all endpoint which requires no model params —
 * org isolation is enforced server-side via the JWT.
 */
export const GetAllPolicyCenterRelatedDocs = async (orgId) => {
  try {
    const params = new URLSearchParams({ organization_id: orgId });
    const response = await API.get(`/policy_center/policies/related/all?${params.toString()}`);
    return response;
  } catch (error) {
    if (error?.response?.status === 204) return { data: [] };
    throw handleApiError(error, 'Failed to get all related documents');
  }
};

